/**
@license
Copyright (c) 2019 Behavio Labs s.r.o. All rights reserved.
*/

import { setPassiveTouchGestures } from '@polymer/polymer/lib/utils/settings';
import { installRouter } from 'pwa-helpers/router';
import { updateMetadata } from 'pwa-helpers/metadata';

import { BaseElement } from './base-element';
import { firebase } from '../modules/firebase-init';
import { signOut } from '../modules/firebase-auth';
import { getUserPermission } from '../modules/back-office-database';

// Core element contains functions for firebase, router, store.
export class CoreElement extends BaseElement {
  constructor() {
    super();

    // The store contains app state.
    this.store = {
      isAppOffline: undefined,
      isUserSignedIn: false,
      page: 'home',
      subPage: null,
      user: {},
      userPermission: {},
    };

    // To force all event listeners for gestures to be passive.
    // See https://www.polymer-project.org/3.0/docs/devguide/settings#setting-passive-touch-gestures
    setPassiveTouchGestures(true);

    firebase.auth().onAuthStateChanged(async user => {
      this._updateStore({ user });
      if (user) {
        this._updateStore({ isUserSignedIn: true });
        this._updateStore({ userPermission: await getUserPermission(user.uid) });
      } else {
        this._updateStore({ isUserSignedIn: false });
      }
      installRouter(location => this._navigate(location));
    });

    this.addEventListener('go-page', event => {
      this._goPage(event.detail);
    });
    this.addEventListener('update-store', event => {
      this._update('store', event.detail);
    });
  }

  _goPage(path) {
    if (`/${path}` !== window.location.pathname) {
      // Update the displayed URL without reloading the page.
      window.history.pushState({}, '', path);
      this._navigate(window.location);
    }
  }

  _loadPage(page, subPage) {
    let finalPage = page;

    switch (page) {
      case 'access-denied':
        // eslint-disable-next-line no-unused-expressions
        import('../views/access-denied-view.js');
        break;
      case 'home':
        import('../views/home-view.js').then(() => {
          // import('../views/home-view.js').then(module => {
          // Put code in here that you want to run every time when
          // navigating to home after home-view.js is loaded.
        });
        break;
      case 'data':
        // eslint-disable-next-line no-unused-expressions
        import('../views/data-view.js');
        break;
      case 'users':
        // eslint-disable-next-line no-unused-expressions
        import('../views/users-view.js');
        break;
      default:
        finalPage = 'not-found';
        // eslint-disable-next-line no-unused-expressions
        import('../views/not-found-view.js');
    }

    this._updateStore({ page: finalPage, subPage });
  }

  _navigate(location) {
    const path = window.decodeURIComponent(location.pathname);
    let page = path === '/' ? 'home' : path.slice(1);
    let subPage = null;

    if (page.includes('/')) [page, subPage] = page.split('/', 2);

    if (page === 'signin.html') {
      if (!this.store.isUserSignedIn) {
        window.location.href = '/signin.html';
        window.history.pushState({}, '', '/');
      } else {
        this._loadPage('not-found');
      }
      return;
    }

    if (page === 'signout') {
      signOut();
      return;
    }

    if (this.store.isUserSignedIn && subPage === 'atlas-cechu' && !this.store.userPermission.mapa_cechu) {
      this._loadPage('access-denied');
      return;
    }

    if (this.store.isUserSignedIn && subPage === 'feg-dashboard' && !this.store.userPermission.feg_dashboard) {
      this._loadPage('access-denied');
      return;
    }

    if (!this.store.isUserSignedIn && ['data', 'users'].indexOf(page) !== -1) {
      this._loadPage('access-denied');
      return;
    }

    this._loadPage(page, subPage);
    // Any other info you might want to extract from the path (like page type),
    // you can do here.

    // Close the drawer - in case the *path* change came from a link in the drawer.
    // this._updateDrawerState(false);
  }

  _storeChanged(oldStore) {
    if (oldStore.page !== this.store.page) {
      const pageTitle = `${this.store.appTitle} - ${this.store.page}`;
      updateMetadata({
        title: pageTitle,
        description: pageTitle,
        // This object also takes an image property, that points to an img src.
      });
    }
  }

  _watchStore(changedProperties) {
    if (changedProperties.has('store')) this._storeChanged(changedProperties.get('store'));
  }
}
